<template>
  <div>
    <DashPeriodFilter
      :period="filters.period"
      :from="filters.from"
      :to="filters.to"
      :applied="appliedPeriod"
      :loading="loading"
      @change="onFilterChange"
    />

    <div v-if="loading && !loaded" class="text-center py-6 text-muted">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p class="mb-0">Carregando...</p>
    </div>

    <div v-else-if="errored" class="text-center py-6 text-muted">
      <i class="fa-solid fa-triangle-exclamation fa-2x mb-3"></i>
      <p class="mb-2">{{ errorMessage || "Não foi possível carregar os dados." }}</p>
      <button class="btn btn-sm btn-outline-primary" @click="fetch">Tentar novamente</button>
    </div>

    <div v-else>
      <div class="alert alert-info d-flex align-items-center mb-3">
        <i class="fa-solid fa-circle-info me-2"></i>
        Cada etapa como % dos pacientes cadastrados naquele mês. Meses recentes
        ainda estão amadurecendo — pontos mais claros nos gráficos abaixo ainda
        não tiveram tempo de completar o funil, compare só entre meses fechados.
      </div>

      <div class="row g-3">
        <div class="col-12 col-xl-6">
          <WidgetCard title="Funil de cadastro no período" :fixed="false">
            <p class="text-muted small mb-3 px-2">
              Cada etapa conta quantos dos pacientes cadastrados no período
              chegaram até ali — não necessariamente nessa ordem.
            </p>
            <div class="funnel">
              <div
                v-for="(band, i) in funnelPyramid"
                :key="band.key"
                class="funnel-band"
                :style="{ width: bandWidth(band.pct), backgroundColor: color(i) }"
              >
                <span class="funnel-band-label">{{ band.label }}</span>
                <span class="funnel-band-value">{{ numberBR(band.count) }} · {{ band.pct }}%</span>
              </div>
            </div>
          </WidgetCard>
        </div>
        <div class="col-12 col-xl-6" v-for="s in data.steps" :key="s.key">
          <WidgetCard :title="stepLabel(s.key)">
            <ChartLine :data="stepSeries(s)" :options="pctOptions" />
          </WidgetCard>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { COLORS, numberBR, singleSeries, baseOptions, dateTicks } from "@/utils/dashboardCharts";
import WidgetCard from "./WidgetCard.vue";
import DashPeriodFilter from "./DashPeriodFilter.vue";

const FUNNEL_STEPS = [
  { key: "viewed_home", label: "Visualizou home" },
  { key: "searched_psys", label: "Buscou psicólogos" },
  { key: "viewed_psy", label: "Viu um psicólogo" },
  { key: "viewed_calendar", label: "Viu o calendário" },
  { key: "viewed_times", label: "Viu horários" },
  { key: "created_appointment", label: "Criou sessão" },
  { key: "credit_card_registered", label: "Adicionou cartão" },
  { key: "concluded_appointment", label: "Concluiu sessão" },
  { key: "checked_coupon", label: "Usou cupom" },
];

export default {
  name: "DashFunnel",
  components: { WidgetCard, DashPeriodFilter },
  data() {
    return {
      loading: false,
      loaded: false,
      errored: false,
      errorMessage: "",
      filters: { period: "last_12_months", from: "", to: "" },
      appliedPeriod: null,
      data: {},
      pctOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ ticks: dateTicks }],
          yAxes: [{ ticks: { beginAtZero: true, max: 100, callback: (v) => `${v}%` } }],
        },
        tooltips: {
          callbacks: {
            label: (item, d) => `${d.datasets[item.datasetIndex].label}: ${item.yLabel}%`,
          },
        },
      },
    };
  },
  computed: {
    funnelPyramid() {
      const total = this.data.total || {};
      const bands = [
        { key: "registered", label: "Cadastrados", count: total.registered || 0, pct: 100 },
      ];
      FUNNEL_STEPS.forEach((s) => {
        if (s.key === "checked_coupon") return;
        const st = total.steps?.[s.key];
        if (!st) return;
        bands.push({ key: s.key, label: s.label, count: st.count, pct: st.pct });
      });
      return bands;
    },
    maturingMonths() {
      return new Set((this.data.cohorts || []).filter((c) => c.maturing).map((c) => c.month));
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    numberBR,
    stepLabel(key) {
      return FUNNEL_STEPS.find((s) => s.key === key)?.label || key;
    },
    stepSeries(step) {
      const rows = step.series || [];
      return singleSeries(rows, "month", "pct", "% do cadastrado no mês", 2, {
        pointBackgroundColor: rows.map((r) => (this.maturingMonths.has(r.month) ? "#fff" : COLORS[2])),
        pointRadius: rows.map((r) => (this.maturingMonths.has(r.month) ? 6 : 3)),
      });
    },
    color(i) {
      return COLORS[i % COLORS.length];
    },
    bandWidth(pct) {
      const w = Math.max(Number(pct) || 0, 22);
      return Math.min(w, 100) + "%";
    },
    onFilterChange(f) {
      this.filters = f;
      this.fetch();
    },
    async fetch() {
      this.loading = true;
      this.errored = false;
      try {
        const res = await this.$dashboardService.funnel(this.filters);
        this.data = res.data || {};
        this.appliedPeriod = res.period || null;
        this.loaded = true;
      } catch (e) {
        this.errored = true;
        this.errorMessage = e.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.funnel {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0;
}
.funnel-band {
  height: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  line-height: 1.1;
  text-align: center;
  overflow: visible;
  white-space: nowrap;
  transition: width 0.3s;
}
.funnel-band:not(:last-child) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
}
.funnel-band-label {
  font-size: 0.78rem;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}
.funnel-band-value {
  font-size: 0.7rem;
  font-weight: 600;
  opacity: 0.95;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}
</style>
